/**
 * leaflet-crs.ts — Utilidades UTM/WGS84 PURAS (sin Leaflet)
 *
 * Funciones de transformación, validación y formato basadas solo en proj4.
 * Se puede importar desde cualquier contexto (componentes React, workers,
 * tests, módulos sin DOM) sin riesgo de crash por Leaflet no disponible.
 *
 * Las constantes y el CRS de Leaflet están en leaflet-crs-map.ts
 *
 * @see https://epsg.io/25830
 */

import proj4 from 'proj4';

// =============================================================================
// DEFINICIONES PROJ4
// =============================================================================

export const EPSG_25830_DEF =
  '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs';

export const WGS84_DEF = '+proj=longlat +datum=WGS84 +no_defs +type=crs';

proj4.defs('EPSG:25830', EPSG_25830_DEF);
proj4.defs('EPSG:4326', WGS84_DEF);

// =============================================================================
// LÍMITES GEOGRÁFICOS
// =============================================================================

export const ANDALUCIA_BOUNDS_UTM = {
  minX: 100000,
  maxX: 620000,
  minY: 3980000,
  maxY: 4290000,
} as const;

export const ANDALUCIA_BOUNDS_WGS84 = {
  minLat: 35.9,
  maxLat: 38.75,
  minLng: -7.55,
  maxLng: -1.63,
} as const;

export const ANDALUCIA_CENTER_WGS84 = { lat: 37.5, lng: -4.5 } as const;
export const ANDALUCIA_CENTER_UTM   = { x: 370000, y: 4150000 } as const;

// =============================================================================
// RESOLUCIONES PARA TILES UTM (necesarias también en leaflet-crs-map.ts)
// =============================================================================

export const UTM_RESOLUTIONS = [
  4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1,
  0.5, 0.25, 0.125, 0.0625, 0.03125, 0.015625, 0.0078125,
] as const;

export const UTM_ORIGIN: [number, number] = [-1000000, 5000000];

// =============================================================================
// TRANSFORMACIONES (puras, proj4 únicamente)
// =============================================================================

export function toUTM30(lat: number, lon: number): [number, number] {
  const r = proj4('EPSG:4326', 'EPSG:25830', [lon, lat]);
  return [r[0], r[1]];
}

export function toWGS84(x: number, y: number): [number, number] {
  const r = proj4('EPSG:25830', 'EPSG:4326', [x, y]);
  return [r[1], r[0]]; // proj4 → [lng, lat]; invertimos a [lat, lng]
}

export function pointToUTM30(p: { lat: number; lng: number }): { x: number; y: number } {
  const [x, y] = toUTM30(p.lat, p.lng);
  return { x, y };
}

export function pointToWGS84(p: { x: number; y: number }): { lat: number; lng: number } {
  const [lat, lng] = toWGS84(p.x, p.y);
  return { lat, lng };
}

// =============================================================================
// VALIDACIÓN
// =============================================================================

export function isValidAndaluciaCoord(x: number, y: number): boolean {
  return (
    x >= ANDALUCIA_BOUNDS_UTM.minX && x <= ANDALUCIA_BOUNDS_UTM.maxX &&
    y >= ANDALUCIA_BOUNDS_UTM.minY && y <= ANDALUCIA_BOUNDS_UTM.maxY
  );
}

export function isValidAndaluciaWGS84(lat: number, lng: number): boolean {
  return (
    lat >= ANDALUCIA_BOUNDS_WGS84.minLat && lat <= ANDALUCIA_BOUNDS_WGS84.maxLat &&
    lng >= ANDALUCIA_BOUNDS_WGS84.minLng && lng <= ANDALUCIA_BOUNDS_WGS84.maxLng
  );
}

export function isValidUTM30Coord(x: number, y: number): boolean {
  return (
    typeof x === 'number' && !isNaN(x) &&
    typeof y === 'number' && !isNaN(y) &&
    x >= 0 && x <= 1000000 &&
    y >= 0 && y <= 10000000
  );
}

// =============================================================================
// UTILIDADES GEOMÉTRICAS
// =============================================================================

export function distanceUTM(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1, dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

export function azimuthUTM(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1, dy = y2 - y1;
  if (dx === 0 && dy === 0) return 0;
  const deg = (Math.atan2(dx, dy) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

const CARDINAL_LABELS = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'] as const;
export type CardinalDirection = typeof CARDINAL_LABELS[number];

export function cardinalLabel(degrees: number): CardinalDirection {
  const normalized = ((degrees % 360) + 360) % 360;
  return CARDINAL_LABELS[Math.round(normalized / 45) % 8];
}

export const CARDINAL_ARROWS: Record<CardinalDirection, string> = {
  N: '↑', NE: '↗', E: '→', SE: '↘',
  S: '↓', SO: '↙', O: '←', NO: '↖',
} as const;

export function formatDragFeedback(ox: number, oy: number, cx: number, cy: number): string {
  const dist = distanceUTM(ox, oy, cx, cy);
  if (dist < 0.5) return '·';
  const dir = cardinalLabel(azimuthUTM(ox, oy, cx, cy));
  const ds = dist < 1000 ? `${Math.round(dist)} m` : `${(dist / 1000).toFixed(1)} km`;
  return `${CARDINAL_ARROWS[dir]} ${ds} ${dir}`;
}

export function formatDisplacement(x1: number, y1: number, x2: number, y2: number): string {
  const dist = distanceUTM(x1, y1, x2, y2);
  if (dist < 0.5) return '0 m';
  const ds = dist < 1000 ? `${Math.round(dist)} m` : `${(dist / 1000).toFixed(2)} km`;
  return `${ds} · ${cardinalLabel(azimuthUTM(x1, y1, x2, y2))} (${Math.round(azimuthUTM(x1, y1, x2, y2))}°)`;
}

export function formatUTM(x: number, y: number, precision = 2): string {
  return `X: ${x.toFixed(precision)}, Y: ${y.toFixed(precision)}`;
}

export function parseUTMString(str: string): { x: number; y: number } | null {
  const patterns = [
    /X?\s*:?\s*([\d.]+)[,\s]+Y?\s*:?\s*([\d.]+)/i,
    /([\d.]+)[,;\s]+([\d.]+)/,
  ];
  for (const p of patterns) {
    const m = str.match(p);
    if (m) {
      const x = parseFloat(m[1]), y = parseFloat(m[2]);
      if (!isNaN(x) && !isNaN(y)) return { x, y };
    }
  }
  return null;
}

export const VERIFICATION_POINTS = {
  catedralSevilla:  { name: 'Catedral de Sevilla',   wgs84: { lat: 37.385894, lng: -5.992866 }, utm30: { x: 235026.39, y: 4141886.41 }, tolerance: 1 },
  alhambraGranada:  { name: 'Alhambra de Granada',   wgs84: { lat: 37.176052, lng: -3.588097 }, utm30: { x: 447794.08, y: 4114564.58 }, tolerance: 1 },
  mezquitaCordoba:  { name: 'Mezquita de Córdoba',   wgs84: { lat: 37.878889, lng: -4.779444 }, utm30: { x: 343506.73, y: 4193869.99 }, tolerance: 1 },
} as const;
